import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Send, Search, MessageSquare, Plus, ArrowLeft, Loader2, X } from 'lucide-react';
import { messaging } from '../lib/api';
import { toast } from 'sonner';

const roleColor = (r) => ({
  admin: 'bg-[#8BC53F]/15 text-[#5b8523]',
  tech: 'bg-[#F59E0B]/15 text-[#b9750a]',
  customer: 'bg-slate-200 text-slate-600',
}[r] || 'bg-slate-200 text-slate-600');

const initials = (name) => (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
const timeFmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z').toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

export default function MessagingView({ heightClass = 'h-[calc(100vh-64px)]', singlePane = false }) {
  const me = (() => { try { return JSON.parse(localStorage.getItem('pp_user') || '{}'); } catch { return {}; } })();
  const myEmail = me?.email;

  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [contacts, setContacts] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [mobileThread, setMobileThread] = useState(false);
  const scrollRef = useRef(null);

  const loadConvos = useCallback(async () => {
    try {
      const data = await messaging.conversations();
      setConvos(data);
    } catch { /* ignore poll errors */ }
    finally { setLoading(false); }
  }, []);

  const loadThread = useCallback(async (cid) => {
    if (!cid) return;
    try {
      const data = await messaging.messages(cid);
      setThread(data);
    } catch { /* ignore */ }
  }, []);

  // initial + polling for conversation list
  useEffect(() => {
    loadConvos();
    const t = setInterval(loadConvos, 6000);
    return () => clearInterval(t);
  }, [loadConvos]);

  // polling for active thread
  useEffect(() => {
    if (!selected) return;
    loadThread(selected.id);
    const t = setInterval(() => loadThread(selected.id), 4000);
    return () => clearInterval(t);
  }, [selected, loadThread]);

  // autoscroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const openConvo = (c) => { setSelected(c); setThread([]); setMobileThread(true); };

  const openContacts = async () => {
    setShowContacts(true);
    try { setContacts(await messaging.contacts()); } catch { toast.error('Could not load contacts'); }
  };

  const startChat = async (contact) => {
    try {
      const convo = await messaging.start(contact.email);
      const shaped = { id: convo.id, other: contact, lastMessage: convo.lastMessage || '', unread: 0, updatedAt: convo.updatedAt };
      setShowContacts(false);
      await loadConvos();
      openConvo(shaped);
    } catch { toast.error('Could not start conversation'); }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !selected) return;
    setSending(true);
    setInput('');
    try {
      const msg = await messaging.send(selected.id, text);
      setThread(prev => [...prev, msg]);
      loadConvos();
    } catch { toast.error('Message failed to send'); setInput(text); }
    finally { setSending(false); }
  };

  const filtered = convos.filter(c => !q || (c.other?.name || '').toLowerCase().includes(q.toLowerCase()));

  const listCls = singlePane
    ? `${mobileThread ? 'hidden' : 'flex'} w-full border-r border-slate-200 bg-white flex-col`
    : `${mobileThread ? 'hidden' : 'flex'} md:flex w-full md:w-[340px] border-r border-slate-200 bg-white flex-col`;
  const threadCls = singlePane
    ? `${mobileThread ? 'flex' : 'hidden'} flex-1 flex-col`
    : `${mobileThread ? 'flex' : 'hidden'} md:flex flex-1 flex-col`;
  const backBtnCls = singlePane ? 'text-slate-600' : 'md:hidden text-slate-600';

  return (
    <div className={`${heightClass} flex bg-slate-50`} data-testid="messaging-view">
      {/* Left: conversation list */}
      <div className={listCls}>
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-extrabold tracking-tight text-slate-900">Messages</h1>
            <Button size="sm" onClick={openContacts} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5 h-8" data-testid="new-message-btn">
              <Plus className="w-4 h-4" />New
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…" className="pl-9 h-9" data-testid="messaging-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 px-6 text-slate-400">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No conversations yet.</p>
              <p className="text-xs mt-1">Tap <span className="font-semibold">New</span> to message a teammate.</p>
            </div>
          ) : filtered.map(c => (
            <button key={c.id} onClick={() => openConvo(c)} data-testid={`conversation-${c.id}`}
              className={`w-full text-left p-4 border-b border-slate-100 hover:bg-slate-50 transition ${selected?.id === c.id ? 'bg-[#8BC53F]/5 border-l-4 border-l-[#8BC53F]' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">{initials(c.other?.name)}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{c.other?.name}</div>
                    <Badge className={`${roleColor(c.other?.role)} border-0 text-[9px] font-bold mt-0.5 capitalize`}>{c.other?.role}</Badge>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] text-slate-500">{timeFmt(c.updatedAt)}</div>
                  {c.unread > 0 && <div className="mt-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#F59E0B] text-white text-[10px] font-bold" data-testid={`unread-${c.id}`}>{c.unread}</div>}
                </div>
              </div>
              {c.lastMessage && <div className="text-xs text-slate-600 mt-2 truncate">{c.lastSenderEmail === myEmail ? 'You: ' : ''}{c.lastMessage}</div>}
            </button>
          ))}
        </div>
      </div>

      {/* Right: thread */}
      <div className={threadCls}>
        {selected ? (
          <>
            <div className="h-16 px-4 md:px-6 border-b border-slate-200 bg-white flex items-center gap-3">
              <button onClick={() => setMobileThread(false)} className={backBtnCls} data-testid="back-to-list"><ArrowLeft className="w-5 h-5" /></button>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] text-white flex items-center justify-center font-bold">{initials(selected.other?.name)}</div>
              <div>
                <div className="font-bold text-slate-900">{selected.other?.name}</div>
                <div className="text-xs text-slate-500 capitalize">{selected.other?.role}</div>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3 bg-slate-50">
              {thread.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">Say hello 👋</div>
              ) : thread.map((m) => {
                const mine = m.senderEmail === myEmail;
                return (
                  <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] md:max-w-md rounded-2xl px-4 py-2.5 ${mine ? 'bg-[#8BC53F] text-white rounded-br-sm' : 'bg-white border border-slate-200 text-slate-900 rounded-bl-sm'}`}>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.text}</div>
                      <div className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-slate-500'}`}>{timeFmt(m.createdAt)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 md:p-4 bg-white border-t border-slate-200 flex gap-2">
              <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())} placeholder="Type a message…" className="h-11" data-testid="message-input" />
              <Button onClick={send} disabled={sending || !input.trim()} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5 h-11 px-5" data-testid="send-message-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <MessageSquare className="w-14 h-14 mb-3 opacity-30" />
            <p className="text-sm">Select a conversation to start chatting</p>
          </div>
        )}
      </div>

      {/* Contact picker */}
      {showContacts && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowContacts(false)} data-testid="contact-picker">
          <div className="bg-white rounded-2xl w-full max-w-sm max-h-[70vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">New Message</h3>
              <button onClick={() => setShowContacts(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {contacts.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No other users yet.</div>
              ) : contacts.map(ct => (
                <button key={ct.email} onClick={() => startChat(ct)} data-testid={`contact-${ct.email}`}
                  className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 border-b border-slate-100 text-left">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] text-white flex items-center justify-center font-bold text-sm">{initials(ct.name)}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{ct.name}</div>
                    <Badge className={`${roleColor(ct.role)} border-0 text-[9px] font-bold capitalize`}>{ct.role}</Badge>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
